import React, { useMemo } from "react";
import { uniqBy } from "lodash";
import dayjs from "dayjs";

import {
  Autocomplete,
  createFilterOptions,
  MenuItem,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { HelpContainer } from "@/components/atoms";
import { useActivationActions } from "@/context";
import { Activation } from "@/types";
import { activationPlatforms, isActivationExpired } from "@/utils";

interface ActivationFormProps {
  activation: Activation;
  activations: Activation[] | null;
  disabled: boolean;
}

interface SeatId {
  name: string;
  inputValue?: string;
}

const ActivationForm: React.FC<ActivationFormProps> = ({
  activation,
  activations,
  disabled,
}) => {
  const activationActions = useActivationActions();

  const seatIds: SeatId[] =
    uniqBy(
      activations?.map((activation) => ({ name: activation.seat_id })),
      "seat_id",
    ) ?? [];
  const filter = createFilterOptions<SeatId>();

  const handleInputChange =
    (field: keyof Activation) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | { value: unknown }>
        | SelectChangeEvent<string>,
    ) => {
      activationActions.change({ [field]: event.target.value as string });
    };

  return (
    <Grid container size={{ xs: 12, md: 8, lg: 6 }} spacing={3}>
      {/* Generic Options  */}
      <Grid size={6}>
        <HelpContainer
          helpText="A name for your activation, this will be used to create the audience in your selected platform"
          disabled={disabled}
        >
          <TextField
            fullWidth
            label="Campaign Name"
            variant="outlined"
            color="secondary"
            size="small"
            value={activation?.campaign_name || ""}
            onChange={handleInputChange("campaign_name")}
            disabled={disabled}
          />
        </HelpContainer>
      </Grid>
      <Grid size={6}>
        <HelpContainer
          helpText="The brand you are building the activation on behalf of"
          disabled={disabled}
        >
          <TextField
            fullWidth
            label="Advertiser Name"
            variant="outlined"
            color="secondary"
            size="small"
            value={activation?.advertiser_name || ""}
            onChange={handleInputChange("advertiser_name")}
            disabled={disabled}
          />
        </HelpContainer>
      </Grid>
      <Grid size={6}>
        <HelpContainer
          helpText="The name of the agency that is running the activation (e.g. your agency)"
          disabled={disabled}
        >
          <TextField
            fullWidth
            label="Agency Name"
            variant="outlined"
            color="secondary"
            size="small"
            value={activation?.agency_name || ""}
            onChange={handleInputChange("agency_name")}
            disabled={disabled}
          />
        </HelpContainer>
      </Grid>
      <Grid size={6}>
        <HelpContainer
          helpText="The name of the person who will be buying against this activation (e.g. your name)"
          disabled={disabled}
        >
          <TextField
            fullWidth
            label="Buyer Name"
            variant="outlined"
            color="secondary"
            size="small"
            value={activation?.buyer_name || ""}
            onChange={handleInputChange("buyer_name")}
            disabled={disabled}
          />
        </HelpContainer>
      </Grid>

      {/* Platform Options  */}
      {activation.campaign_type === "eyeota" && (
        <>
          <Grid size={6}>
            <HelpContainer
              helpText="The platform you wish to activate on"
              disabled={disabled}
            >
              <TextField
                fullWidth
                label="Platform"
                variant="outlined"
                color="secondary"
                size="small"
                select
                value={activation?.platform || ""}
                onChange={handleInputChange("platform")}
                disabled={disabled}
              >
                {activationPlatforms[
                  activation.campaign_type as keyof typeof activationPlatforms
                ].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </HelpContainer>
          </Grid>
          <Grid size={6}>
            <Autocomplete
              disablePortal
              selectOnFocus
              clearOnBlur
              freeSolo
              options={seatIds}
              value={activation?.seat_id || ""}
              onChange={(_event, newValue) => {
                if (typeof newValue === "string") {
                  activationActions.change({ seat_id: newValue });
                } else if (newValue && newValue.inputValue) {
                  activationActions.change({ seat_id: newValue.inputValue });
                } else {
                  newValue &&
                    activationActions.change({ seat_id: newValue.name });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some(
                  (option) => inputValue === option.name,
                );
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    inputValue,
                    name: `Add "${inputValue}"`,
                  });
                }

                return filtered;
              }}
              getOptionLabel={(option) => {
                // Value selected with enter, right from the input
                if (typeof option === "string") {
                  return option;
                }
                // Add "xxx" option created dynamically
                if (option.inputValue) {
                  return option.inputValue;
                }
                // Regular option
                return option.name;
              }}
              renderInput={(params) => (
                <HelpContainer
                  helpText="Your unique identifier for the selected platform"
                  disabled={disabled}
                >
                  <TextField
                    {...params}
                    fullWidth
                    label="Seat ID"
                    variant="outlined"
                    color="secondary"
                    size="small"
                  />
                </HelpContainer>
              )}
              disabled={disabled}
            />
          </Grid>
        </>
      )}

      {/* DV360 Options  */}

      {activation?.platform === "DV360" && (
        <Grid container size={12}>
          <Grid size={4}>
            <HelpContainer
              helpText="The DV360 Campaign Type: Display or Video"
              disabled={disabled}
            >
              <TextField
                fullWidth
                label="Campaign Type"
                variant="outlined"
                color="secondary"
                size="small"
                select
                value={activation?.dv_campaign_type || ""}
                onChange={handleInputChange("dv_campaign_type")}
                disabled={disabled}
              >
                <MenuItem value="Display">Display</MenuItem>
                <MenuItem value="Video">Video</MenuItem>
                <MenuItem value="Display & Video">Display & Video</MenuItem>
                <MenuItem value="DigitalOOH">DigitalOOH</MenuItem>
              </TextField>
            </HelpContainer>
          </Grid>
          <Grid size={4}>
            <HelpContainer
              helpText="The DV360 ID Type: Partner, Advertiser, or Ads Manager"
              disabled={disabled}
            >
              <TextField
                fullWidth
                label="ID Type"
                variant="outlined"
                color="secondary"
                size="small"
                select
                value={activation?.dv_id_type || ""}
                onChange={handleInputChange("dv_id_type")}
                disabled={disabled}
              >
                <MenuItem value="Partner ID">Partner</MenuItem>
                <MenuItem value="Advertiser">Advertiser</MenuItem>
                <MenuItem value="Ads Manager">Ads Manager</MenuItem>
              </TextField>
            </HelpContainer>
          </Grid>
          <Grid size={4}>
            <HelpContainer
              helpText="The DV360 Currency (e.g. GBP)"
              disabled={disabled}
            >
              <TextField
                fullWidth
                label="Currency"
                variant="outlined"
                color="secondary"
                size="small"
                value={activation?.dv_currency || ""}
                onChange={handleInputChange("dv_currency")}
                disabled={disabled}
              />
            </HelpContainer>
          </Grid>
        </Grid>
      )}

      {/* Date Options  */}
      <Grid size={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <HelpContainer
            helpText="The date at which the activation will start"
            disabled={disabled}
          >
            <DatePicker
              label="Start Date"
              value={dayjs(activation?.campaign_date_start, "YYYY-MM-DD")}
              format="DD-MM-YYYY"
              disablePast
              onChange={(newValue) => {
                newValue &&
                  activationActions.change({ campaign_date_start: newValue.format("YYYY-MM-DD") });
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  color: "secondary",
                  error: false,
                },
                field: { clearable: true },
              }}
              disabled={disabled}
            />
          </HelpContainer>
        </LocalizationProvider>
      </Grid>
      <Grid size={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <HelpContainer
            helpText="The date at which the activation will end. Leave blank for an always on campaign."
            disabled={disabled}
          >
            <DatePicker
              label="End Date (optional)"
              value={dayjs(activation?.campaign_date_end, "YYYY-MM-DD")}
              format="DD-MM-YYYY"
              disablePast
              minDate={dayjs(activation?.campaign_date_start, "YYYY-MM-DD")}
              onChange={(newValue) => {
                newValue &&
                  activationActions.change({ campaign_date_end: newValue.format("YYYY-MM-DD") });
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  color: "secondary",
                  error: isActivationExpired(activation),
                  helperText: isActivationExpired(activation)
                    ? "This activation has expired."
                    : "",
                },
                field: { clearable: true },
              }}
              disabled={disabled}
            />
          </HelpContainer>
        </LocalizationProvider>
      </Grid>
    </Grid>
  );
};

export default ActivationForm;
